/**
 * Note: These functions will be defined by Anaheim. If they are not present
 * we will use the polyfilled version below.
 */
declare global {
  interface Window {
    [key: string]: any;
    getWindowSegments?: Function;
    onBoundingWindowRectsChange: Event;
    CustomEvent: Function;
    DOMRect: Function;
  }

  interface External {
    getHostEnvironmentValue: Function;
  }
}

const config = window["__foldables_env_vars__"];

export class WindowSegment {
  left: number;
  right: number;
  top: number;
  bottom: number;
  x: number;
  y: number;
  width: number;
  height: number;
  isOccluded: boolean;
  toJSON(): string {
    return JSON.stringify(this);
  }

  constructor(
    left: number,
    top: number,
    width: number,
    height: number,
    isOccluded: boolean = false
  ) {
    this.left = left;
    this.right = left + width;
    this.top = top;
    this.bottom = top + height;
    this.x = left;
    this.y = top;
    this.width = width;
    this.height = height;
    this.pixelDepth = 0;
    this.devicePixelRatio = 0;
    this.isOccluded = isOccluded;
  }

  readonly pixelDepth: number;
  readonly devicePixelRatio: number;
}

export function ensureWindowsAPIs(): void {
  ensureOnBoundingWindowRectsChange();
  ensureGetWindowSegments();
}

function ensureOnBoundingWindowRectsChange(): void {
  if (typeof window.onBoundingWindowRectsChange === "object") {
    return;
  }

  window.onBoundingWindowRectsChange = new CustomEvent(
    "onBoundingWindowRectsChange",
    {
      bubbles: false,
      cancelable: false,
    }
  );

  window.addEventListener("resize", fireBoundingWindowRectsChanged);
}

function fireBoundingWindowRectsChanged(): void {
  window.dispatchEvent(window.onBoundingWindowRectsChange);
}

function ensureGetWindowSegments(): void {
  if (typeof window.getWindowSegments === "function") {
    return;
  }

  const getWindowSegments = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;

    // Edge crashes intermittently (typeError: invalid procedure call or argument) when
    // making the getHostEvironmentValue call. This safegaurds against that... Even if it worked,
    // it would still use the last return statement (which is what should happen if this fails).
    // See: https://office.visualstudio.com/OC/_workitems/edit/3877085
    try {
      // Check if we are on the PRODUCT_LITE SKU (e.g. 189 (or 0xBD))
      if (
        window.external &&
        window.external.getHostEnvironmentValue &&
        window.external.getHostEnvironmentValue("os-sku") === '{"os-sku":"189"}'
      ) {
        // Double Landscape
        // The orientation is based off of the first screen.
        // and we know we are in the right browser because the dimensions are actually portrait
        // The height is taking into account the browser chrome which varies between Edge and Anaheim
        if (
          window.matchMedia &&
          window.matchMedia("(orientation: portrait)").matches &&
          height >= 1300 &&
          width === 964
        ) {
          config && config.update({ spanning: "single-fold-horizontal" });
          return [
            new WindowSegment(0, 0, width, height / 2),
            new WindowSegment(0, height / 2, width, height / 2),
          ];
        }

        // Double Portrait
        // The orientation is based off of the first screen
        // and we know we are in the right browser because the dimensions are actually landscape.
        // The height is taking into account the browser chrome which varies between Edge and Anaheim
        if (
          window.matchMedia &&
          window.matchMedia("(orientation: landscape)").matches &&
          height >= 820 &&
          width === 1440
        ) {
          config && config.update({ spanning: "single-fold-vertical" });
          return [
            new WindowSegment(0, 0, width / 2, height),
            new WindowSegment(width / 2, 0, width / 2, height),
          ];
        }

        config && config.update({ spanning: "none" });
      }
    } catch (e: any) {
      // There's some errors in Edge that happen intermittently
      // (typeError: invalid procedure call or argument for the getHostEnvironmentValue),
      // this catches that error. See:
      // https://office.visualstudio.com/OC/_workitems/edit/3877085
    }

    // Single
    return [new WindowSegment(0, 0, width, height)];
  };

  window.getWindowSegments = getWindowSegments;
}
